import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Plus, Eye, Edit, Trash2, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { API, axios } from './adminConfig';

const EventsTab = ({ events, categories, setCategories, fetchEvents }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [newCategory, setNewCategory] = useState('');

  const toggleEventVisibility = async (eventId, currentVisibility) => {
    try {
      const newVisibility = !currentVisibility;
      await axios.patch(`${API}/admin/events/${eventId}/visibility`, { visible: newVisibility });
      toast.success(`Event ${newVisibility ? 'published' : 'hidden'} successfully!`);
      fetchEvents();
    } catch (error) {
      console.error('Error toggling event visibility:', error);
      toast.error('Error updating event visibility');
    }
  };

  const addCategory = () => {
    if (newCategory.trim()) {
      setCategories(prev => ({
        ...prev,
        events: [...prev.events, newCategory.trim().toLowerCase().replace(/\s+/g, '_')]
      }));
      setNewCategory('');
      toast.success('Event category added successfully!');
    }
  };

  const removeCategory = (categoryToRemove) => {
    setCategories(prev => ({
      ...prev,
      events: prev.events.filter(cat => cat !== categoryToRemove)
    }));
    toast.success('Event category removed successfully!');
  };

  const [globalEventsEnabled, setGlobalEventsEnabled] = useState(() => {
    return localStorage.getItem('eventsEnabled') === 'true';
  });

  const toggleGlobalEventsVisibility = () => {
    const newValue = !globalEventsEnabled;
    setGlobalEventsEnabled(newValue);
    localStorage.setItem('eventsEnabled', newValue.toString());
    toast.success(`Events section ${newValue ? 'enabled' : 'disabled'} globally!`);
    
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  return (
    <div className="space-y-6">
      {/* Global Events Toggle */}
      <Card className="bg-white border-burgundy/20">
        <CardHeader>
          <CardTitle className="font-serif text-burgundy">Global Events Settings</CardTitle>
          <CardDescription>Control whether the Events section is visible to users</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-burgundy">Events Section Visibility</h3>
              <p className="text-sm text-burgundy/60">
                {globalEventsEnabled 
                  ? 'Events section is currently visible to all users in navigation' 
                  : 'Events section is currently hidden from user navigation'
                }
              </p>
            </div>
            <Button
              onClick={toggleGlobalEventsVisibility}
              className={globalEventsEnabled 
                ? 'bg-orange-500 hover:bg-orange-600 text-white' 
                : 'bg-emerald-500 hover:bg-emerald-600 text-white'
              }
            >
              {globalEventsEnabled ? 'Hide Events' : 'Show Events'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-burgundy font-serif">Events Management</h2>
        <Button onClick={() => setShowAddForm(true)} className="bg-burgundy hover:bg-burgundy/80 text-cream">
          <Plus className="h-4 w-4 mr-2" />
          Create Event
        </Button>
      </div>

      {/* Event Categories */}
      <Card className="bg-white border-burgundy/20">
        <CardHeader>
          <CardTitle className="font-serif text-burgundy">Event Categories</CardTitle>
          <CardDescription>Manage categories for events and workshops</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-4">
            {categories.events.map(category => (
              <Badge key={category} variant="outline" className="border-dark-teal text-dark-teal">
                {category.replace('_', ' ')}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeCategory(category)}
                  className="ml-2 h-4 w-4 p-0 hover:bg-red-100"
                >
                  <X className="h-3 w-3" />
                </Button>
              </Badge>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              placeholder="New event category"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              className="border-burgundy/30 focus:border-gold"
            />
            <Button onClick={addCategory} className="bg-gold hover:bg-gold/80 text-burgundy">
              Add Category
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Events List */}
      <div className="grid grid-cols-1 gap-6">
        {events.map(event => (
          <Card key={event.id} className="bg-white border-burgundy/20">
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold text-burgundy">{event.title}</h3>
                    <Badge className={`${event.visible ? 'bg-emerald-100 text-emerald-800' : 'bg-orange-100 text-orange-800'}`}>
                      {event.visible ? 'Published' : 'Hidden'}
                    </Badge>
                  </div>
                  <p className="text-burgundy/70 mb-3">{event.description}</p>
                  <div className="flex gap-4 text-sm text-burgundy/60">
                    <span>📅 {event.date}</span>
                    <span>⏰ {event.time}</span>
                    <span>👥 Capacity: {event.capacity}</span>
                    <Badge variant="outline">{event.type}</Badge>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleEventVisibility(event.id, event.visible)}
                    className={event.visible ? 'border-orange-300 text-orange-600' : 'border-emerald-300 text-emerald-600'}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setEditingEvent(event)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" className="border-red-300 text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export { EventsTab };
